import React, { useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Paper } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Plot from 'react-plotly.js';

export const ProfilingResults = ({ data }) => {
  const [graphOpen, setGraphOpen] = useState(false);
  const [graphColumn, setGraphColumn] = useState(null);

  if (!data || !data.profiling_results || data.profiling_results.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No profiling results available</p>
      </div>
    );
  }

  // Get the first profiling result (assuming we're showing one asset's results)
  const profilingResult = data.profiling_results[0];
  const columns = profilingResult.columns || [];

  const SimpleStatCard = ({ title, value, color = '#bb1ccc' }) => (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        padding: 3,
        width: '200px',
        height: '120px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: '1px solid #d3d3d3',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-4px)',
          border: '1px solid #bb1ccc'
        }
      }}
    >
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color, fontSize: '1.1rem' }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            height: '3px',
            width: '40px',
            backgroundColor: color,
            borderRadius: '2px',
            mt: 0.5,
          }}
        />
      </Box>

      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <div>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
        <SimpleStatCard title="Columns Analyzed" value={columns.length} color="#bb1ccc" />
        <SimpleStatCard title="Total Rows" value={columns[0]?.count || "N/A"} color="#bb1ccc" />
      </Box>

      {/* Columns Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          marginTop: "20px",
          maxHeight: '500px',
          overflow: 'auto'
        }}
      >
        <Table sx={{ minWidth: 650 }} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                color: "white",
                fontWeight: 600,
                fontSize: '14px',
                padding: '16px 20px',
                borderBottom: '2px solid #000080',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#bb1ccc !important'
              }}>
                Column
              </TableCell>
              <TableCell sx={{
                color: "white",
                fontWeight: 600,
                fontSize: '14px',
                padding: '16px 20px',
                borderBottom: '2px solid #000080',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#bb1ccc !important'
              }}>
                Data Type
              </TableCell>
              <TableCell sx={{
                color: "white",
                fontWeight: 600,
                fontSize: '14px',
                padding: '16px 20px',
                borderBottom: '2px solid #000080',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#bb1ccc !important'
              }}>
                CDE
              </TableCell>
              <TableCell sx={{
                color: "white",
                fontWeight: 600,
                fontSize: '14px',
                padding: '16px 20px',
                borderBottom: '2px solid #000080',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#bb1ccc !important'
              }}>
                Business Term
              </TableCell>
              <TableCell sx={{
                color: "white",
                fontWeight: 600,
                fontSize: '14px',
                padding: '16px 20px',
                borderBottom: '2px solid #000080',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#bb1ccc !important'
              }}>
                Null %
              </TableCell>
              <TableCell sx={{
                color: "white",
                fontWeight: 600,
                fontSize: '14px',
                padding: '16px 20px',
                borderBottom: '2px solid #000080',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#bb1ccc !important'
              }}>
                Statistics
              </TableCell>
              <TableCell sx={{
                color: "white",
                fontWeight: 600,
                fontSize: '14px',
                padding: '16px 20px',
                borderBottom: '2px solid #000080',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: '#bb1ccc !important'
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.map((col, index) => (
              <TableRow
                key={col.column_name}
                sx={{
                  backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                  '&:hover': {
                    backgroundColor: '#f0f8ff',
                    transition: 'background-color 0.2s ease'
                  }
                }}
              >
                <TableCell sx={{
                  padding: '16px 20px',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#2c3e50'
                }}>
                  {col.column_name}
                </TableCell>
                <TableCell sx={{
                  padding: '16px 20px',
                  fontSize: '14px',
                  color: '#555'
                }}>
                  {col.type}
                </TableCell>
                <TableCell sx={{
                  padding: '16px 20px',
                  fontSize: '14px'
                }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      backgroundColor: col.isCde ? '#e8f5e8' : '#fff3e0',
                      color: col.isCde ? '#2e7d32' : '#f57c00'
                    }}
                  >
                    {col.isCde ? "Yes" : "No"}
                  </Box>
                </TableCell>
                <TableCell sx={{
                  padding: '16px 20px',
                  fontSize: '14px',
                  color: '#555'
                }}>
                  {col.businessterm === null || col.businessterm === "N/A" ? "N/A" : col.businessterm}
                </TableCell>
                <TableCell sx={{
                  padding: '16px 20px',
                  fontSize: '14px',
                  color: '#555'
                }}>
                  {col.nullPercentage === null ? "N/A" : `${col.nullPercentage || 0}%`}
                </TableCell>
                <TableCell sx={{
                  padding: '16px 20px',
                  fontSize: '14px',
                  color: '#555'
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span><strong>Count:</strong> {col.count || "N/A"}</span>
                    <span><strong>Min:</strong> {col.min === null ? "N/A" : col.min}</span>
                    <span><strong>Max:</strong> {col.max === null ? "N/A" : col.max}</span>
                    {col.mean !== null && <span><strong>Mean:</strong> {col.mean}</span>}
                  </Box>
                </TableCell>
                <TableCell sx={{
                  padding: '16px 20px',
                  fontSize: '14px'
                }}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      color: '#bb1ccc',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#f0f8ff'
                      }
                    }}
                    onClick={() => { 
                      console.log('🔍 View Plot clicked for column:', col.column_name);
                      console.log('🔍 Column data:', col);
                      console.log('🔍 Chart data:', col.chart);
                      setGraphOpen(true); 
                      setGraphColumn(col); 
                    }}
                  >
                    View Plot
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Plotly Graph Popup */}
      <Dialog 
        open={graphOpen} 
        onClose={() => setGraphOpen(false)} 
        maxWidth="lg" 
        fullWidth
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle sx={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <div className='flex justify-between items-center'>
            <span className="text-lg font-semibold">
              Chart: {graphColumn?.column_name || 'Column'} - Value Distribution
            </span>
            <IconButton onClick={() => setGraphOpen(false)}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent sx={{ p: 3, minHeight: '600px' }}>
          {graphColumn?.chart ? (
            <div style={{ width: '100%', height: '100%' }}>
              {console.log('🔍 Chart data:', graphColumn.chart)}
              {console.log('🔍 Chart data array:', graphColumn.chart.data)}
              {console.log('🔍 Chart layout:', graphColumn.chart.layout)}
              
              <Plot
                data={graphColumn.chart.data || []}
                layout={{
                  ...graphColumn.chart.layout,
                  margin: { t: 80, b: 100, l: 80, r: 80 },
                  autosize: true,
                  width: undefined,
                  height: 500,
                  showlegend: true,
                  title: {
                    text: graphColumn.chart.layout?.title || 'Value Frequency',
                    font: { size: 18, color: '#2c3e50' }
                  },
                  xaxis: {
                    ...graphColumn.chart.layout?.xaxis,
                    title: { text: 'Values', font: { size: 14 } },
                    tickangle: -45
                  },
                  yaxis: {
                    ...graphColumn.chart.layout?.yaxis,
                    title: { text: 'Count', font: { size: 14 } }
                  }
                }}
                style={{ width: '100%', height: '500px' }}
                useResizeHandler={true}
                config={{
                  displayModeBar: true,
                  displaylogo: false,
                  modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
                  responsive: true
                }}
              />
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 text-lg">No chart data available for this column</p>
              <p className="text-sm text-gray-500 mt-2">
                Chart data: {JSON.stringify(graphColumn?.chart, null, 2)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}; 